import { DOMAttributes, MutableRefObject, useCallback, useEffect, useRef } from 'react';
import useThrottledCallback from '../../../utils/react/useThrottledCallback';
import { getRailPositionFromXPos, getSliderValueFromRailPosition, shouldSnapToCheckpoint } from '../sliderUtils';
import { SliderProps } from '../Slider';
import useDebouncedCallback from '../../../utils/react/useDebouncedCallback';

type Props = SliderProps & {
    currentValue: number;
    setCurrentValue: (currentValue: number) => void;
    sliderHandleContainerRectRef: MutableRefObject<DOMRect | null>;
};

const useSliderHandleEventHandlers = (props: Props): DOMAttributes<HTMLButtonElement> => {
    const { currentValue, setCurrentValue, checkpoint, resetValue, min, max, sliderHandleContainerRectRef } = props;

    const startingRailPosition = useRef<number | null>(null);
    const prevRailPosition = useRef<number | null>(null);

    const debouncedSetStartingRailPosition = useDebouncedCallback(
        (railPosition: number) => {
            startingRailPosition.current = railPosition;
        },
        100,
        [],
    );

    const updateStartingRailPosition = useCallback((railPosition: number) => {
        // Re-set starting rail position if the user stop moving their cursor for 100ms
        debouncedSetStartingRailPosition(railPosition);

        if (prevRailPosition.current === null || startingRailPosition.current === null) return;

        // Re-set starting rail position to the furthest rail position if user reverse their cursor movement
        // e.g. User move from 0.5 to 0.8, then move back to 0.6, update startingRailPosition to be 0.8
        if (
            (prevRailPosition.current > railPosition && prevRailPosition.current > startingRailPosition.current) ||
            (prevRailPosition.current < railPosition && prevRailPosition.current < startingRailPosition.current)
        ) {
            startingRailPosition.current = prevRailPosition.current;
        }
    }, []);

    const onPointerMove = useThrottledCallback(
        (event: MouseEvent) => {
            if (!sliderHandleContainerRectRef.current) return;
            if (prevRailPosition.current === null || startingRailPosition.current === null) return;

            const railPosition = getRailPositionFromXPos(event.pageX, sliderHandleContainerRectRef.current);

            const shouldSnapToCP =
                checkpoint && shouldSnapToCheckpoint(startingRailPosition, prevRailPosition, railPosition, props);

            const newValue = shouldSnapToCP ? checkpoint : getSliderValueFromRailPosition(railPosition, props);
            if (newValue !== currentValue) {
                setCurrentValue(newValue);
            }

            updateStartingRailPosition(railPosition);

            prevRailPosition.current = railPosition;
        },
        50,
        [currentValue, min, max, checkpoint],
    );

    const onPointerUp = useCallback(() => {
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);

        prevRailPosition.current = null;
        startingRailPosition.current = null;
    }, []);

    const onPointerDown = useCallback((event) => {
        if (!sliderHandleContainerRectRef.current) return;

        const currentRailPosition = getRailPositionFromXPos(event.pageX, sliderHandleContainerRectRef.current);
        prevRailPosition.current = currentRailPosition;
        startingRailPosition.current = currentRailPosition;

        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    }, []);

    const onDoubleClick = useCallback(() => {
        resetValue && setCurrentValue(resetValue);
    }, [resetValue]);

    // Clean up the event listeners when component is unmounted
    useEffect(() => {
        return () => {
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
        };
    }, []);

    return {
        onPointerDown,
        onDoubleClick,
    };
};

export default useSliderHandleEventHandlers;
